import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { FileText, Download, X, Save, Plus, ChevronDown, Check } from "lucide-react";
import { formatLocalDateTime, formatLocalTime } from "@/utils/formatters";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper function to parse date strings in multiple formats
const parseDateString = (dateStr) => {
  if (!dateStr) return null;
  
  // Check if DD/MM/YYYY format
  if (dateStr.includes('/')) {
    const [day, month, year] = dateStr.split('/');
    return new Date(year, parseInt(month) - 1, day);
  }
  
  // Check if YYYY-MM-DD format or other standard formats
  const date = new Date(dateStr);
  if (!isNaN(date.getTime())) {
    return date;
  }
  
  return null;
};

const Planning = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [flights, setFlights] = useState([]);
  const [filteredFlights, setFilteredFlights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFlight, setSelectedFlight] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [agents, setAgents] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [editingAssignment, setEditingAssignment] = useState({});
  
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isAgency = currentUser.role === 'agency';
  const isStaff = currentUser.role === 'staff';
  const isAdmin = currentUser.role === 'admin';
  
  // Filter states - default to upcoming flights with all months
  const currentDate = new Date();
  const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear().toString());
  const [selectedMonth, setSelectedMonth] = useState("all"); // Changed to "all" for upcoming flights filter
  const [selectedAgency, setSelectedAgency] = useState("all");
  const [selectedAgents, setSelectedAgents] = useState([]); // Array for multiple agent selection
  // Staff users default to "last30days", others to "upcoming"
  const [dateFilter, setDateFilter] = useState(isStaff ? "last30days" : "upcoming"); // "all", "past", "upcoming", "last30days"
  const [sortOrder, setSortOrder] = useState("asc"); // "asc" = nearest first (default), "desc" = newest first

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [flights, searchParams, selectedYear, selectedMonth, selectedAgency, selectedAgents, dateFilter, sortOrder]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [flightsRes, agentsRes, agenciesRes] = await Promise.all([
        axios.get(`${API}/flights`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/agents`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/agencies`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      // The backend already filters flights for staff users, so we get only assigned flights
      setFlights(flightsRes.data);
      setAgents(agentsRes.data);
      setAgencies(agenciesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error("Failed to fetch data");
    }
    setLoading(false);
  };

  const applyFilters = () => {
    const filter = searchParams.get('filter');
    let filtered = [...flights];
    
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset to start of today

    // NOTE: Backend already filters flights for staff/agency users
    // No additional filtering needed here - just apply date filters

    // Apply URL filter (today or tomorrow)
    if (filter === 'today') {
      // Format today as DD/MM/YYYY to match database format
      const now = new Date();
      const todayStr = `${String(now.getDate()).padStart(2, '0')}/${String(now.getMonth() + 1).padStart(2, '0')}/${now.getFullYear()}`;
      filtered = filtered.filter(f => f.flight_date === todayStr);
    } else if (filter === 'tomorrow') {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      // Format tomorrow as DD/MM/YYYY to match database format
      const tomorrowStr = `${String(tomorrow.getDate()).padStart(2, '0')}/${String(tomorrow.getMonth() + 1).padStart(2, '0')}/${tomorrow.getFullYear()}`;
      filtered = filtered.filter(f => f.flight_date === tomorrowStr);
    }
    
    // Apply Date Range filter (upcoming, past, all, last30days) - only if not using today/tomorrow filter
    if (!filter || (filter !== 'today' && filter !== 'tomorrow')) {
      if (dateFilter === "upcoming") {
        filtered = filtered.filter(flight => {
          const flightDate = parseDateString(flight.flight_date);
          if (!flightDate) return false;
          flightDate.setHours(0, 0, 0, 0);
          return flightDate >= today;
        });
      } else if (dateFilter === "past") {
        filtered = filtered.filter(flight => {
          const flightDate = parseDateString(flight.flight_date);
          if (!flightDate) return false;
          flightDate.setHours(0, 0, 0, 0);
          return flightDate < today;
        });
      } else if (dateFilter === "last30days") {
        // Last 30 days + all future flights
        const thirtyDaysAgo = new Date(today);
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        thirtyDaysAgo.setHours(0, 0, 0, 0);
        
        filtered = filtered.filter(flight => {
          const flightDate = parseDateString(flight.flight_date);
          if (!flightDate) return false;
          flightDate.setHours(0, 0, 0, 0);
          // Show flights from last 30 days onwards (including all future)
          return flightDate >= thirtyDaysAgo;
        });
      }
      // If "all", no date filter applied
    }
    
    // Apply Year and Month filters (only if not using today/tomorrow filter)
    if (!filter || (filter !== 'today' && filter !== 'tomorrow')) {
      filtered = filtered.filter(flight => {
        const flightDate = parseDateString(flight.flight_date);
        if (!flightDate) return false;
        
        const flightYear = flightDate.getFullYear();
        const flightMonth = flightDate.getMonth() + 1;
        
        // Year filter
        if (selectedYear !== "all" && flightYear.toString() !== selectedYear) {
          return false;
        }
        
        // Month filter
        if (selectedMonth !== "all" && flightMonth.toString() !== selectedMonth) {
          return false;
        }
        
        return true;
      });
    }
    
    // Apply Agency filter (only for admin/agency users, not staff)
    if (!isStaff && selectedAgency !== "all") {
      filtered = filtered.filter(f => {
        // Safety check: only filter if flight has valid agency_id
        if (!f || !f.agency_id) return false;
        return f.agency_id === selectedAgency;
      });
    }
    
    // Apply Agent filter (only for admin users, not staff or agency)
    // Now supports multiple agent selection
    if (!isStaff && !isAgency && selectedAgents.length > 0) {
      filtered = filtered.filter(f => {
        // Check if flight has any of the selected agents assigned
        // Support both agent_names (array) and agent_name (single) for backward compatibility
        const flightAgents = f.agent_names && f.agent_names.length > 0 
          ? f.agent_names 
          : (f.agent_name ? [f.agent_name] : []);
        
        // Show flight if any of the selected agents is assigned to it
        return flightAgents.some(agent => selectedAgents.includes(agent));
      });
    }

    // Sort by date first, then by time when dates are the same
    filtered.sort((a, b) => {
      const dateA = parseDateString(a.flight_date);
      const dateB = parseDateString(b.flight_date);
      
      // Handle invalid dates - push them to the end
      if (!dateA || !dateB) {
        if (!dateA && !dateB) return 0;
        if (!dateA) return 1;
        if (!dateB) return -1;
      }
      
      // Compare dates only (ignore time component) by comparing year, month, day
      const yearA = dateA.getFullYear();
      const yearB = dateB.getFullYear();
      const monthA = dateA.getMonth();
      const monthB = dateB.getMonth();
      const dayA = dateA.getDate();
      const dayB = dateB.getDate();
      
      // Compare year first
      if (yearA !== yearB) {
        return sortOrder === "desc" ? yearB - yearA : yearA - yearB;
      }
      // Then month
      if (monthA !== monthB) {
        return sortOrder === "desc" ? monthB - monthA : monthA - monthB;
      }
      // Then day
      if (dayA !== dayB) {
        return sortOrder === "desc" ? dayB - dayA : dayA - dayB;
      }
      
      // Dates are the same - sort by flight time
      // Normalize times to HH:MM format for consistent comparison
      const normalizeTime = (time) => {
        if (!time) return '00:00';
        // Extract HH:MM from HH:MM or HH:MM:SS format
        const parts = time.split(':');
        return `${parts[0].padStart(2, '0')}:${(parts[1] || '00').padStart(2, '0')}`;
      };
      const timeA = normalizeTime(a.flight_time);
      const timeB = normalizeTime(b.flight_time);
      return sortOrder === "desc" ? timeB.localeCompare(timeA) : timeA.localeCompare(timeB);
    });
    
    setFilteredFlights(filtered);
  };

  const handleFlightClick = (flight) => {
    setSelectedFlight(flight);
    setDetailsOpen(true);
  };
  
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    // If already in DD/MM/YYYY format, return as-is
    if (dateString.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
      return dateString;
    }
    
    try {
      // Handle YYYY-MM-DD format
      if (dateString.includes('-')) {
        const [year, month, day] = dateString.split('-');
        return `${day}/${month}/${year}`;
      }
      return dateString;
    } catch {
      return dateString;
    }
  };
  
  const formatTime = (timeString) => {
    if (!timeString) return '';
    try {
      // If time includes seconds (HH:MM:SS), remove them
      const parts = timeString.split(':');
      if (parts.length >= 2) {
        return `${parts[0]}:${parts[1]}`;
      }
      return timeString;
    } catch {
      return timeString;
    }
  };


  const handleAddServices = () => {
    // Store flight data in localStorage so Flights page can access it
    localStorage.setItem('openServiceDialog', JSON.stringify({
      flightId: selectedFlight.id,
      flightNumber: selectedFlight.flight_number
    }));
    
    // Close the details dialog
    setDetailsOpen(false);
    
    // Navigate to Flights page
    navigate('/flights');
  };


  const handleDownloadFile = async (flightId, fileId, filename) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/flights/${flightId}/files/${fileId}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Check if response is valid
      if (!response.data || response.data.size === 0) {
        console.error('Empty file received');
        toast.error("File is empty or not found");
        return;
      }
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success("File downloaded successfully");
    } catch (error) {
      console.error('Download error:', error);
      let errorMsg = "Failed to download file";
      if (error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const json = JSON.parse(text);
          errorMsg = json.detail || errorMsg;
        } catch (e) { /* ignore parse errors */ }
      } else {
        errorMsg = error.response?.data?.detail || error.message || errorMsg;
      }
      toast.error(errorMsg);
    }
  };

  const handleExportPassengerList = async (flightId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/flights/${flightId}/passenger-list-pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Passenger_List.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success("Passenger list downloaded");
    } catch (error) {
      toast.error("Failed to export passenger list");
    }
  };

  const handleAssignAgent = async (flightId, agentNames, dutyTime) => {
    try {
      const token = localStorage.getItem('token');
      // Support both single agent (backward compatibility) and multiple agents
      const agentsArray = Array.isArray(agentNames) ? agentNames : [agentNames].filter(Boolean);
      
      await axios.patch(`${API}/flights/${flightId}`, {
        assigned_staff_duty_time: dutyTime,
        agent_name: agentsArray.length > 0 ? agentsArray[0] : null, // Keep first agent for backward compatibility
        agent_names: agentsArray
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(agentsArray.length > 1 ? "Agents assigned successfully" : "Agent assigned successfully");
      fetchData();
      setEditingAssignment({});
    } catch (error) {
      toast.error("Failed to assign agent(s)");
    }
  };

  const calculateDutyTime = (flightDate, flightTime) => {
    if (!flightDate || !flightTime) return '';
    
    try {
      // Parse the flight date correctly
      const date = parseDateString(flightDate);
      if (!date) return '';
      
      // Parse time
      const [hours, minutes] = flightTime.split(':');
      date.setHours(parseInt(hours) - 2, parseInt(minutes), 0, 0);
      
      return date.toISOString().slice(0, 16);
    } catch (error) {
      console.error('Error calculating duty time:', error);
      return '';
    }
  };

  if (loading) return <div style={{ color: '#B0B0B0' }}>Loading...</div>;

  const filter = searchParams.get('filter');
  let pageTitle = isStaff ? "My Assigned Flights" : "Global Planning";
  let pageSubtitle = isStaff ? "Flights assigned to you" : "All Past and Future Flights";
  
  if (filter === 'today') {
    pageTitle = isStaff ? "My Flights Today" : "Today's Flights";
    pageSubtitle = isStaff ? "Your flights scheduled for today" : "Flights scheduled for today";
  } else if (filter === 'tomorrow') {
    pageTitle = isStaff ? "My Flights Tomorrow" : "Tomorrow's Flights";
    pageSubtitle = isStaff ? "Your flights scheduled for tomorrow" : "Flights scheduled for tomorrow";
  }
  
  // Generate year options (current year ± 2 years)
  const currentYear = new Date().getFullYear();
  const yearOptions = [];
  for (let i = currentYear - 2; i <= currentYear + 2; i++) {
    yearOptions.push(i.toString());
  }
  
  // Month options
  const monthOptions = [
    { value: "1", label: "January" },
    { value: "2", label: "February" },
    { value: "3", label: "March" },
    { value: "4", label: "April" },
    { value: "5", label: "May" },
    { value: "6", label: "June" },
    { value: "7", label: "July" },
    { value: "8", label: "August" },
    { value: "9", label: "September" },
    { value: "10", label: "October" },
    { value: "11", label: "November" },
    { value: "12", label: "December" }
  ];

  return (
    <div>
      <div className="mb-6 flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold" style={{ color: '#FFFFFF' }}>{pageTitle}</h1>
          <p className="mt-1" style={{ color: '#B0B0B0' }}>{pageSubtitle}</p>
          {filteredFlights.length > 0 && (
            <p className="mt-2 text-sm" style={{ color: '#FFD700' }}>
              Showing {filteredFlights.length} flight{filteredFlights.length !== 1 ? 's' : ''}
            </p>
          )}
        </div>
        <Button
          onClick={() => {
            fetchData();
            toast.success("Data refreshed successfully");
          }}
          style={{ 
            background: '#3B82F6', 
            color: '#FFFFFF',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"/>
          </svg>
          Refresh Times
        </Button>
      </div>
      
      {/* Date Range Filter Buttons */}
      <div className="mb-4 flex gap-2 items-center">
        <Button
          variant={dateFilter === "upcoming" ? "default" : "outline"}
          onClick={() => {
            setDateFilter("upcoming");
            setSelectedMonth("all");
          }}
          style={dateFilter === "upcoming" ? 
            { background: '#22C55E', color: '#FFFFFF' } : 
            { borderColor: '#4A5A8A', color: '#E8E8E8', background: 'transparent' }
          }
        >
          Upcoming Flights
        </Button>
        <Button
          variant={dateFilter === "all" ? "default" : "outline"}
          onClick={() => {
            setDateFilter("all");
            setSelectedMonth("all");
          }}
          style={dateFilter === "all" ? 
            { background: '#60A5FA', color: '#FFFFFF' } : 
            { borderColor: '#4A5A8A', color: '#E8E8E8', background: 'transparent' }
          }
        >
          All Flights
        </Button>
        <Button
          variant={dateFilter === "past" ? "default" : "outline"}
          onClick={() => {
            setDateFilter("past");
            setSelectedMonth("all");
          }}
          style={dateFilter === "past" ? 
            { background: '#9CA3AF', color: '#FFFFFF' } : 
            { borderColor: '#4A5A8A', color: '#E8E8E8', background: 'transparent' }
          }
        >
          Past Flights
        </Button>
        {isStaff && (
          <Button
            variant={dateFilter === "last30days" ? "default" : "outline"}
            onClick={() => {
              setDateFilter("last30days");
              setSelectedMonth("all");
            }}
            style={dateFilter === "last30days" ? 
              { background: '#10B981', color: '#FFFFFF' } : 
              { borderColor: '#4A5A8A', color: '#E8E8E8', background: 'transparent' }
            }
          >
            Last 30 Days + Future
          </Button>
        )}
        
        {/* Sort Order Toggle Button */}
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ color: '#B0B0B0', fontSize: '14px' }}>Sort:</span>
          <Button
            variant="outline"
            onClick={() => {
              setSortOrder(sortOrder === "desc" ? "asc" : "desc");
            }}
            style={{ 
              borderColor: '#4A5A8A', 
              color: '#FFD700', 
              background: 'rgba(255, 215, 0, 0.1)',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontWeight: 'bold'
            }}
          >
            {sortOrder === "desc" ? (
              <>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="m3 16 4 4 4-4"/>
                  <path d="M7 20V4"/>
                  <path d="m21 8-4-4-4 4"/>
                  <path d="M17 4v16"/>
                </svg>
                Newest First
              </>
            ) : (
              <>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="m3 8 4-4 4 4"/>
                  <path d="M7 4v16"/>
                  <path d="m21 16-4 4-4-4"/>
                  <path d="M17 20V4"/>
                </svg>
                Oldest First
              </>
            )}
          </Button>
        </div>
      </div>
      
      {/* Filter Controls */}
      <Card className="mb-6" style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label style={{ color: '#E8E8E8' }}>Year</Label>
              <Select value={selectedYear} onValueChange={setSelectedYear}>
                <SelectTrigger style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
                  <SelectItem value="all">All Years</SelectItem>
                  {yearOptions.map(year => (
                    <SelectItem key={year} value={year}>{year}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label style={{ color: '#E8E8E8' }}>Month</Label>
              <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                <SelectTrigger style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
                  <SelectItem value="all">All Months</SelectItem>
                  {monthOptions.map(month => (
                    <SelectItem key={month.value} value={month.value}>{month.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Agency Filter - Hide for agency and staff users */}
            {!isAgency && !isStaff && (
              <div>
                <Label style={{ color: '#E8E8E8' }}>Agency</Label>
                <Select value={selectedAgency} onValueChange={setSelectedAgency}>
                  <SelectTrigger style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }}>
                    <SelectValue placeholder="Select Agency" />
                  </SelectTrigger>
                  <SelectContent style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
                    <SelectItem value="all">All Agencies</SelectItem>
                    {Array.isArray(agencies) && agencies
                      .filter(agency => agency && agency.id && agency.name && typeof agency.name === 'string')
                      .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                      .map(agency => (
                        <SelectItem key={agency.id} value={agency.id}>{agency.name}</SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {/* Agent Filter - Multi-select with checkboxes - Hide for agency and staff users */}
            {!isAgency && !isStaff && (
              <div>
                <Label style={{ color: '#E8E8E8' }}>Agent</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      className="w-full justify-between"
                      style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }}
                    >
                      {selectedAgents.length === 0 
                        ? "All Agents" 
                        : selectedAgents.length === 1 
                          ? selectedAgents[0]
                          : `${selectedAgents.length} agents selected`}
                      <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[250px] p-0" style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
                    <div className="p-2">
                      <div 
                        className="flex items-center gap-2 p-2 cursor-pointer hover:bg-opacity-20 hover:bg-white rounded"
                        onClick={() => setSelectedAgents([])}
                      >
                        <div className="w-4 h-4 border rounded flex items-center justify-center" style={{ borderColor: '#4A5A8A' }}>
                          {selectedAgents.length === 0 && <Check className="h-3 w-3" style={{ color: '#4ade80' }} />}
                        </div>
                        <span style={{ color: '#E8E8E8' }}>All Agents</span>
                      </div>
                      <div className="border-t my-1" style={{ borderColor: '#4A5A8A' }} />
                      {Array.isArray(agents) && agents
                        .filter(agent => agent && agent.name && typeof agent.name === 'string')
                        .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                        .map(agent => (
                          <div
                            key={agent.id}
                            className="flex items-center gap-2 p-2 cursor-pointer hover:bg-opacity-20 hover:bg-white rounded"
                            onClick={() => {
                              setSelectedAgents(prev => 
                                prev.includes(agent.name)
                                  ? prev.filter(n => n !== agent.name)
                                  : [...prev, agent.name]
                              );
                            }}
                          >
                            <Checkbox 
                              checked={selectedAgents.includes(agent.name)}
                              className="border-gray-500"
                            />
                            <span style={{ color: '#E8E8E8' }}>{agent.name}</span>
                          </div>
                        ))}
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
            )}
            
            <div className="flex items-end">
              <Button 
                onClick={() => {
                  setSelectedYear(currentDate.getFullYear().toString());
                  setSelectedMonth("all");
                  setSelectedAgency("all");
                  setSelectedAgents([]);
                  setDateFilter("upcoming");
                }}
                variant="outline"
                style={{ borderColor: '#4A5A8A', color: '#E8E8E8', width: '100%' }}
              >
                Reset Filters
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="overflow-x-auto rounded-lg" style={{ background: '#1E2A4A', border: '1px solid #4A5A8A' }}>
        <table className="w-full">
          <thead>
            <tr style={{ background: 'rgba(30, 42, 74, 0.8)', borderBottom: '1px solid #4A5A8A' }}>
              <th className="p-3 text-left" style={{ color: '#FFFFFF' }}>Date</th>
              <th className="p-3 text-left" style={{ color: '#FFFFFF' }}>Time</th>
              <th className="p-3 text-left" style={{ color: '#FFFFFF' }}>Flight</th>
              <th className="p-3 text-left" style={{ color: '#FFFFFF' }}>Status</th>
              <th className="p-3 text-left" style={{ color: '#FFFFFF' }}>Airline</th>
              <th className="p-3 text-left" style={{ color: '#FFFFFF' }}>Type</th>
              <th className="p-3 text-left" style={{ color: '#FFFFFF' }}>Route</th>
              <th className="p-3 text-left" style={{ color: '#FFFFFF' }}>PAX</th>
              <th className="p-3 text-left" style={{ color: '#FFFFFF' }}>Actual PAX</th>
              <th className="p-3 text-left" style={{ color: '#FFFFFF' }}>Actual Dep.</th>
              <th className="p-3 text-left" style={{ color: '#FFFFFF' }}>Agency</th>
              {/* Show agent columns only for non-staff users */}
              {!isStaff && (
                <>
                  {isAgency ? (
                    <th className="p-3 text-left" style={{ color: '#FFFFFF' }}>Assigned Agent</th>
                  ) : (
                    <>
                      <th className="p-3 text-left" style={{ color: '#FFFFFF' }}>Agent</th>
                      <th className="p-3 text-left" style={{ color: '#FFFFFF' }}>Duty Time</th>
                    </>
                  )}
                </>
              )}
              <th className="p-3 text-left" style={{ color: '#FFFFFF' }}>Files</th>
              <th className="p-3 text-center" style={{ color: '#FFFFFF' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredFlights.map((flight, idx) => (
              <tr 
                key={flight.id} 
                style={{ 
                  borderBottom: '1px solid rgba(74, 90, 138, 0.3)', 
                  background: idx % 2 === 0 ? 'rgba(10, 17, 40, 0.4)' : 'rgba(30, 42, 74, 0.2)' 
                }}
              >
                <td className="p-3" style={{ color: '#E8E8E8' }}>{formatDate(flight.flight_date)}</td>
                <td className="p-3" style={{ color: '#B0B0B0' }}>{formatTime(flight.flight_time) || 'N/A'}</td>
                <td className="p-3">
                  <a 
                    href={`https://planefinder.net/flight/${flight.flight_number.toUpperCase()}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:underline"
                    style={{ color: '#60A5FA', fontWeight: 'bold' }}
                  >
                    {flight.flight_number}
                  </a>
                </td>
                <td className="p-3">
                  {/* Show status badge for all flights that have approval_status */}
                  {flight.approval_status ? (
                    <span className="px-2 py-1 rounded text-xs font-semibold" style={{ 
                      background: flight.approval_status === 'approved' ? 'rgba(34, 197, 94, 0.2)' : 
                                 flight.approval_status === 'pending' ? 'rgba(251, 191, 36, 0.2)' : 
                                 'rgba(239, 68, 68, 0.2)',
                      color: flight.approval_status === 'approved' ? '#4ade80' : 
                             flight.approval_status === 'pending' ? '#fbbf24' : 
                             '#f87171'
                    }}>
                      {flight.approval_status === 'approved' ? 'Approved' : 
                       flight.approval_status === 'pending' ? 'Pending' : 
                       flight.approval_status === 'rejected' ? 'Rejected' : 
                       '-'}
                    </span>
                  ) : (
                    <span className="px-2 py-1 rounded text-xs font-semibold" style={{ 
                      background: 'rgba(34, 197, 94, 0.2)',
                      color: '#4ade80'
                    }}>
                      Approved
                    </span>
                  )}
                </td>
                <td className="p-3" style={{ color: '#B0B0B0' }}>
                  {flight.airline || 'N/A'}
                </td>
                <td className="p-3">
                  <span className="px-2 py-1 rounded text-xs" style={{ 
                    background: flight.flight_type === 'arrival' ? 'rgba(34, 197, 94, 0.2)' : 'rgba(239, 68, 68, 0.2)',
                    color: flight.flight_type === 'arrival' ? '#4ade80' : '#f87171'
                  }}>
                    {flight.flight_type}
                  </span>
                </td>
                <td className="p-3" style={{ color: '#B0B0B0' }}>
                  {flight.departure_airport || 'N/A'} → {flight.arrival_airport || 'N/A'}
                </td>
                <td className="p-3" style={{ color: '#E8E8E8' }}>
                  {flight.passenger_count}
                </td>
                <td className="p-3" style={{ color: flight.actual_passenger_count ? '#FFD700' : '#B0B0B0' }}>
                  {flight.actual_passenger_count || '-'}
                </td>
                <td className="p-3" style={{ color: flight.atd ? '#FFD700' : '#B0B0B0', fontSize: '11px' }}>
                  {flight.atd && flight.atd !== 'N/A' ? formatLocalTime(flight.atd) : '-'}
                </td>
                <td className="p-3" style={{ color: '#B0B0B0' }}>{flight.agency_name}</td>
                {/* Show agent columns only for non-staff users */}
                {!isStaff && (
                  <>
                    {isAgency ? (
                      <>
                        {/* Agency users see assigned agents as read-only text */}
                        <td className="p-3" style={{ color: '#E8E8E8' }}>
                          {flight.agent_names && flight.agent_names.length > 0 
                            ? flight.agent_names.join(', ')
                            : flight.agent_name || <span style={{ color: '#B0B0B0' }}>Not assigned</span>}
                        </td>
                      </>
                    ) : (
                      <>
                        {/* Admin users see multi-agent assignment with checkboxes */}
                        <td className="p-3" onClick={(e) => e.stopPropagation()}>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                className="w-full justify-between text-left"
                                style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8', height: '32px', fontSize: '11px', minWidth: '120px' }}
                              >
                                {(() => {
                                  const currentAgents = editingAssignment[flight.id]?.agents 
                                    || flight.agent_names 
                                    || (flight.agent_name ? [flight.agent_name] : []);
                                  if (currentAgents.length === 0) return 'Assign...';
                                  if (currentAgents.length === 1) return currentAgents[0];
                                  return `${currentAgents.length} agents`;
                                })()}
                                <ChevronDown className="ml-1 h-3 w-3 shrink-0 opacity-50" />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-[200px] p-0" style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
                              <div className="p-2 max-h-[250px] overflow-y-auto">
                                {agents.sort((a, b) => a.name.localeCompare(b.name)).map(agent => {
                                  const currentAgents = editingAssignment[flight.id]?.agents 
                                    || flight.agent_names 
                                    || (flight.agent_name ? [flight.agent_name] : []);
                                  const isChecked = currentAgents.includes(agent.name);
                                  
                                  return (
                                    <div
                                      key={agent.id}
                                      className="flex items-center gap-2 p-2 cursor-pointer hover:bg-opacity-20 hover:bg-white rounded"
                                      onClick={() => {
                                        const newAgents = isChecked
                                          ? currentAgents.filter(n => n !== agent.name)
                                          : [...currentAgents, agent.name];
                                        setEditingAssignment({
                                          ...editingAssignment, 
                                          [flight.id]: {
                                            ...editingAssignment[flight.id], 
                                            agents: newAgents
                                          }
                                        });
                                      }}
                                    >
                                      <Checkbox 
                                        checked={isChecked}
                                        className="border-gray-500"
                                      />
                                      <span style={{ color: '#E8E8E8', fontSize: '12px' }}>
                                        {agent.name} {agent.role && <span style={{ color: '#B0B0B0' }}>({agent.role})</span>}
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>
                            </PopoverContent>
                          </Popover>
                        </td>
                        <td className="p-3" onClick={(e) => e.stopPropagation()}>
                          <div className="flex gap-1 items-center">
                            <Input
                              type="datetime-local"
                              value={editingAssignment[flight.id]?.dutyTime || flight.assigned_staff_duty_time || calculateDutyTime(flight.flight_date, flight.flight_time)}
                              onChange={(e) => setEditingAssignment({...editingAssignment, [flight.id]: {...editingAssignment[flight.id], dutyTime: e.target.value}})}
                              style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8', height: '32px', fontSize: '11px', width: '140px' }}
                            />
                            {(editingAssignment[flight.id]?.agents || editingAssignment[flight.id]?.dutyTime) && (
                              <Button
                                size="sm"
                                onClick={() => handleAssignAgent(
                                  flight.id, 
                                  editingAssignment[flight.id]?.agents || flight.agent_names || (flight.agent_name ? [flight.agent_name] : []),
                                  editingAssignment[flight.id]?.dutyTime || flight.assigned_staff_duty_time
                                )}
                                style={{ background: '#22c55e', color: '#FFFFFF', height: '32px', padding: '0 8px' }}
                              >
                                <Save className="h-3 w-3" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </>
                    )}
                  </>
                )}
                <td className="p-3" style={{ color: '#C0C0C0' }}>
                  {flight.files?.length > 0 ? (
                    <div className="flex items-center gap-2">
                      <span className="flex items-center">
                        <FileText className="h-4 w-4 mr-1" />
                        {flight.files.length}
                      </span>
                      {/* Download Files button for staff */}
                      {isStaff && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            // Download all files for this flight
                            flight.files.forEach(file => {
                              handleDownloadFile(flight.id, file.id, file.filename);
                            });
                          }}
                          style={{ 
                            background: 'rgba(96, 165, 250, 0.2)', 
                            borderColor: '#60A5FA', 
                            color: '#60A5FA',
                            fontSize: '11px',
                            padding: '2px 8px',
                            height: '24px'
                          }}
                        >
                          Download
                        </Button>
                      )}
                    </div>
                  ) : '-'}
                </td>
                <td className="p-3 text-center">
                  <Button
                    size="sm"
                    onClick={() => handleFlightClick(flight)}
                    style={{ background: '#FFD700', color: '#0A1128', fontSize: '12px', padding: '4px 12px' }}
                  >
                    Show Details
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      {filteredFlights.length === 0 && (
        <div className="text-center py-12">
          <p style={{ color: '#B0B0B0' }}>No flights found</p>
        </div>
      )}

      {/* Flight Details Dialog */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent 
          className="max-w-3xl max-h-[90vh] overflow-y-auto" 
          style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}
        >
          <DialogHeader>
            <div className="flex flex-col items-center gap-3">
              <DialogTitle style={{ color: '#FFD700', fontSize: '24px' }}>
                {selectedFlight?.flight_number}
              </DialogTitle>
              {!isStaff && (
                <Button
                  onClick={handleAddServices}
                  size="sm"
                  style={{ background: '#22C55E', color: '#FFFFFF' }}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Add Services
                </Button>
              )}
            </div>
          </DialogHeader>
          
          {selectedFlight && (
            <div className="space-y-4">
              {/* Flight Info */}
              <Card style={{ background: 'rgba(10, 17, 40, 0.6)', border: '1px solid rgba(74, 90, 138, 0.3)' }}>
                <CardContent className="pt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm" style={{ color: '#B0B0B0' }}>Date & Time</p>
                      <p className="font-medium" style={{ color: '#E8E8E8' }}>
                        {formatDate(selectedFlight.flight_date)} {formatTime(selectedFlight.flight_time)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm" style={{ color: '#B0B0B0' }}>Type</p>
                      <p className="font-medium capitalize" style={{ color: '#E8E8E8' }}>{selectedFlight.flight_type}</p>
                    </div>
                    <div>
                      <p className="text-sm" style={{ color: '#B0B0B0' }}>Route</p>
                      <p className="font-medium" style={{ color: '#E8E8E8' }}>
                        {selectedFlight.departure_airport} → {selectedFlight.arrival_airport}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm" style={{ color: '#B0B0B0' }}>Passengers</p>
                      <p className="font-medium" style={{ color: '#E8E8E8' }}>{selectedFlight.passenger_count}</p>
                    </div>
                    <div>
                      <p className="text-sm" style={{ color: '#B0B0B0' }}>Agency</p>
                      <p className="font-medium" style={{ color: '#E8E8E8' }}>{selectedFlight.agency_name}</p>
                    </div>
                    <div>
                      <p className="text-sm" style={{ color: '#B0B0B0' }}>Airline</p>
                      <p className="font-medium" style={{ color: '#E8E8E8' }}>{selectedFlight.airline || 'N/A'}</p>
                    </div>
                    {selectedFlight.group_name && (
                      <div>
                        <p className="text-sm" style={{ color: '#B0B0B0' }}>Group Name</p>
                        <p className="font-medium" style={{ color: '#E8E8E8' }}>{selectedFlight.group_name}</p>
                      </div>
                    )}
                    {selectedFlight.tour_leader_name && (
                      <div>
                        <p className="text-sm" style={{ color: '#B0B0B0' }}>Tour Leader Name</p>
                        <p className="font-medium" style={{ color: '#E8E8E8' }}>{selectedFlight.tour_leader_name}</p>
                      </div>
                    )}
                    {selectedFlight.tour_leader_phone && (
                      <div>
                        <p className="text-sm" style={{ color: '#B0B0B0' }}>Tour Leader Phone</p>
                        <p className="font-medium" style={{ color: '#E8E8E8' }}>{selectedFlight.tour_leader_phone}</p>
                      </div>
                    )}
                    {selectedFlight.agency_emergency_contact && (
                      <div>
                        <p className="text-sm" style={{ color: '#B0B0B0' }}>Emergency Contact</p>
                        <p className="font-medium" style={{ color: '#E8E8E8' }}>{selectedFlight.agency_emergency_contact}</p>
                      </div>
                    )}
                  </div>
                  
                  {selectedFlight.pnr_list && selectedFlight.pnr_list.length > 0 && (
                    <div className="mt-4">
                      <p className="text-sm mb-1" style={{ color: '#B0B0B0' }}>PNRs</p>
                      <p className="font-medium" style={{ color: '#E8E8E8' }}>{selectedFlight.pnr_list.join(', ')}</p>
                    </div>
                  )}
                  
                  {selectedFlight.special_requests && (
                    <div className="mt-4">
                      <p className="text-sm mb-1" style={{ color: '#B0B0B0' }}>Special Requests</p>
                      <p className="font-medium" style={{ color: '#E8E8E8' }}>{selectedFlight.special_requests}</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Operational Report Data */}
              {(selectedFlight.actual_passenger_count || selectedFlight.actual_departure_time || selectedFlight.no_show_list?.length > 0 || selectedFlight.go_show_list?.length > 0 || selectedFlight.gate_number || selectedFlight.operational_notes) && (
                <Card style={{ background: 'rgba(10, 17, 40, 0.6)', border: '1px solid rgba(74, 90, 138, 0.3)' }}>
                  <CardContent className="pt-4">
                    <h3 className="text-lg font-semibold mb-3" style={{ color: '#FFD700' }}>Operational Report Data</h3>
                    <div className="space-y-3">
                      {selectedFlight.actual_passenger_count && (
                        <div>
                          <p className="text-sm font-semibold" style={{ color: '#B0B0B0' }}>Actual Passenger Count</p>
                          <p className="font-medium" style={{ color: '#E8E8E8' }}>{selectedFlight.actual_passenger_count}</p>
                        </div>
                      )}
                      {selectedFlight.atd && (
                        <div>
                          <p className="text-sm font-semibold" style={{ color: '#B0B0B0' }}>Actual Departure Time</p>
                          <p className="font-medium" style={{ color: '#E8E8E8' }}>
                            {formatLocalDateTime(selectedFlight.atd)}
                          </p>
                        </div>
                      )}
                      {selectedFlight.gate_number && (
                        <div>
                          <p className="text-sm font-semibold" style={{ color: '#B0B0B0' }}>Gate Number</p>
                          <p className="font-medium" style={{ color: '#E8E8E8' }}>{selectedFlight.gate_number}</p>
                        </div>
                      )}
                      {selectedFlight.no_show_list && selectedFlight.no_show_list.length > 0 && (
                        <div>
                          <p className="text-sm font-semibold" style={{ color: '#B0B0B0' }}>No-Show Passengers</p>
                          <div className="mt-2 space-y-1">
                            {selectedFlight.no_show_list.map((passenger, idx) => (
                              <p key={idx} className="text-sm" style={{ color: '#f87171' }}>• {passenger}</p>
                            ))}
                          </div>
                        </div>
                      )}
                      {selectedFlight.go_show_list && selectedFlight.go_show_list.length > 0 && (
                        <div>
                          <p className="text-sm font-semibold" style={{ color: '#B0B0B0' }}>Go-Show Passengers</p>
                          <div className="mt-2 space-y-1">
                            {selectedFlight.go_show_list.map((passenger, idx) => (
                              <p key={idx} className="text-sm" style={{ color: '#4ade80' }}>• {passenger}</p>
                            ))}
                          </div>
                        </div>
                      )}
                      {selectedFlight.operational_notes && (
                        <div>
                          <p className="text-sm font-semibold" style={{ color: '#B0B0B0' }}>Operational Notes</p>
                          <p className="text-sm mt-1" style={{ color: '#E8E8E8', whiteSpace: 'pre-wrap' }}>{selectedFlight.operational_notes}</p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Services - Hidden for staff users only */}
              {!isStaff && selectedFlight.services && selectedFlight.services.length > 0 && (
                <Card style={{ background: 'rgba(10, 17, 40, 0.6)', border: '1px solid rgba(74, 90, 138, 0.3)' }}>
                  <CardContent className="pt-4">
                    <h3 className="text-lg font-semibold mb-3" style={{ color: '#FFD700' }}>Services</h3>
                    <div className="space-y-2">
                      {selectedFlight.services.map((service, idx) => (
                        <div key={idx} className="p-3 rounded" style={{ background: 'rgba(255, 255, 255, 0.05)' }}>
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <p className="font-medium" style={{ color: '#E8E8E8' }}>{service.service_name}</p>
                              {service.assigned_staff && (
                                <p className="text-sm mt-1" style={{ color: '#B0B0B0' }}>
                                  Agent: {service.assigned_staff}
                                </p>
                              )}
                              {service.notes && (
                                <p className="text-sm mt-1" style={{ color: '#B0B0B0' }}>
                                  Notes: {service.notes}
                                </p>
                              )}
                            </div>
                            <div className="text-right ml-4">
                              <p className="font-semibold" style={{ color: '#FFD700' }}>€{service.price?.toFixed(2)}</p>
                              {service.parking_fee && service.parking_fee > 0 && (
                                <p className="text-sm mt-1" style={{ color: '#B0B0B0' }}>
                                  Parking: €{service.parking_fee.toFixed(2)}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {/* Totals Breakdown */}
                    <div className="mt-4 pt-4 space-y-2" style={{ borderTop: '1px solid rgba(74, 90, 138, 0.3)' }}>
                      <div className="flex justify-between items-center">
                        <p className="font-medium" style={{ color: '#E8E8E8' }}>Services Total</p>
                        <p className="font-semibold" style={{ color: '#E8E8E8' }}>
                          €{(selectedFlight.services.reduce((sum, s) => sum + (s.price || 0), 0)).toFixed(2)}
                        </p>
                      </div>
                      {selectedFlight.services.some(s => s.parking_fee && s.parking_fee > 0) && (
                        <div className="flex justify-between items-center">
                          <p className="font-medium" style={{ color: '#E8E8E8' }}>Parking Fees Total</p>
                          <p className="font-semibold" style={{ color: '#E8E8E8' }}>
                            €{(selectedFlight.services.reduce((sum, s) => sum + (s.parking_fee || 0), 0)).toFixed(2)}
                          </p>
                        </div>
                      )}
                      <div className="flex justify-between items-center pt-2" style={{ borderTop: '1px solid rgba(74, 90, 138, 0.2)' }}>
                        <p className="text-lg font-bold" style={{ color: '#FFFFFF' }}>Total To Be Invoiced</p>
                        <p className="text-2xl font-bold" style={{ color: '#FFD700' }}>
                          €{(
                            selectedFlight.services.reduce((sum, s) => sum + (s.price || 0) + (s.parking_fee || 0), 0)
                          ).toFixed(2)}
                        </p>
                      </div>
                      
                      {/* VAT Notice */}
                      <div className="mt-4 pt-3 space-y-1" style={{ borderTop: '1px solid rgba(74, 90, 138, 0.3)', background: 'rgba(255, 215, 0, 0.1)', padding: '12px', borderRadius: '6px' }}>
                        <p className="text-xs font-semibold" style={{ color: '#FFD700' }}>VAT Information:</p>
                        <p className="text-xs leading-relaxed" style={{ color: '#E8E8E8' }}>
                          All prices are exempt Belgian VAT. Intracommunity companies are not subject to VAT. 
                          Exemptions envisaged by Article 44 of the Belgian VAT Code.
                        </p>
                        <p className="text-xs font-medium mt-2" style={{ color: '#FFD700' }}>
                          For Belgian Companies: 21% VAT will be added to invoice.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Attached Files */}
              {selectedFlight.files && selectedFlight.files.length > 0 && (
                <Card style={{ background: 'rgba(10, 17, 40, 0.6)', border: '1px solid rgba(74, 90, 138, 0.3)' }}>
                  <CardContent className="pt-4">
                    <h3 className="text-lg font-semibold mb-3" style={{ color: '#FFD700' }}>Attached Files</h3>
                    <div className="space-y-2">
                      {selectedFlight.files.map((file, idx) => (
                        <div 
                          key={idx} 
                          className="flex items-center justify-between p-3 rounded hover:bg-opacity-80 transition-all"
                          style={{ background: 'rgba(255, 255, 255, 0.05)' }}
                        >
                          <div className="flex items-center gap-3">
                            <FileText className="h-5 w-5" style={{ color: '#FFD700' }} />
                            <div>
                              <p className="font-medium" style={{ color: '#E8E8E8' }}>{file.filename}</p>
                              <p className="text-xs" style={{ color: '#B0B0B0' }}>
                                {(file.file_size / 1024).toFixed(2)} KB
                              </p>
                            </div>
                          </div>
                          <button
                            onClick={() => handleDownloadFile(selectedFlight.id, file.id, file.filename)}
                            className="p-2 rounded hover:bg-opacity-20"
                            style={{ background: 'rgba(255, 215, 0, 0.1)', color: '#FFD700' }}
                          >
                            <Download className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Planning;
